import { Alert, Anchor, Badge, Card, Group, SimpleGrid, Stack, Table, Text } from '@mantine/core';
import type { AdminSystemHealthResponse } from '@prepzio/shared';

import { InfoTip } from '../components/InfoTip';
import { Sparkline } from '../components/Sparkline';
import { useSystemHealth } from '../hooks/use-content';
import { useI18n, useT, type TranslateFn } from '../i18n';
import { errorMessage } from '../lib/ui';

/** Datadog `overall_state` → a colour. Only the unhappy states get one. */
function monitorColor(status: string): string {
  if (status === 'Alert') return 'red';
  if (status === 'Warn') return 'yellow';
  if (status === 'No Data') return 'gray';
  if (status === 'OK') return 'green';
  return 'gray';
}

/** Deep link into the one place the panel deliberately does not reproduce. */
function traceUrl(traceId: string): string {
  return `https://app.datadoghq.com/apm/trace/${encodeURIComponent(traceId)}`;
}

function fmtAgo(ms: number | null, t: TranslateFn): string {
  if (ms == null) return t('common.never');
  const mins = Math.round((Date.now() - ms) / 60000);
  if (mins < 1) return t('health.justNow');
  if (mins < 60) return t('health.minutesAgo', { n: mins });
  const hours = Math.round(mins / 60);
  if (hours < 48) return t('health.hoursAgo', { n: hours });
  return t('health.daysAgo', { n: Math.round(hours / 24) });
}

function pct(n: number): string {
  return `${Math.round(n * 100)}%`;
}

function usd(n: number, locale: string): string {
  return `$${n.toLocaleString(locale, { minimumFractionDigits: n < 1 ? 4 : 2, maximumFractionDigits: 4 })}`;
}

function fmtSeen(ms: number | null, locale: string): string {
  if (ms == null) return '—';
  const d = new Date(ms);
  return Number.isNaN(d.getTime())
    ? '—'
    : d.toLocaleString(locale, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
      });
}

function issueStateColor(state: string): string {
  const s = state.toUpperCase();
  if (s === 'OPEN' || s === 'REGRESSED') return 'red';
  if (s === 'RESOLVED') return 'green';
  return 'gray';
}

function Stat({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <Card withBorder padding="md" radius="md">
      <Text size="xs" c="dimmed" tt="uppercase" fw={600}>
        {label}
      </Text>
      <Text fw={700} fz={24} mt={2}>
        {value}
      </Text>
      {sub && (
        <Text size="xs" c="dimmed">
          {sub}
        </Text>
      )}
    </Card>
  );
}

export function HealthScreen() {
  const t = useT();
  const { data, isLoading, isError, error } = useSystemHealth();

  if (isError)
    return (
      <Text c="red" size="sm">
        {errorMessage(error)}
      </Text>
    );
  if (isLoading || !data)
    return (
      <Text c="dimmed" size="sm">
        {t('common.loading')}
      </Text>
    );

  return <HealthBody data={data} />;
}

function HealthBody({ data }: { data: AdminSystemHealthResponse }) {
  const { t, locale } = useI18n();
  const { datadog } = data;
  const alerting = datadog.monitors.filter((m) => m.status === 'Alert' || m.status === 'Warn');

  return (
    <Stack gap="md">
      <Group gap={6} align="center">
        <Text c="dimmed" size="sm">
          {t('health.intro', { hours: data.windowHours, env: data.env })}
        </Text>
        <InfoTip label={t('health.infoTip', { env: data.env })} />
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md">
        <Stat
          label={t('health.llmCalls')}
          value={String(data.llm.calls)}
          sub={t('health.ledgerWindow', { hours: data.windowHours })}
        />
        <Stat
          label={t('health.llmErrors')}
          value={String(data.llm.errors)}
          sub={t('health.llmErrorsSub')}
        />
        <Stat
          label={t('health.fallbackRate')}
          value={pct(data.llm.fallbackRate)}
          sub={t('health.fallbackSub', { fallbacks: data.llm.fallbacks, calls: data.llm.calls })}
        />
        <Stat
          label={t('health.llmP95')}
          value={data.llm.p95LatencyMs == null ? '—' : `${data.llm.p95LatencyMs} ms`}
          sub={t('health.fromLedger')}
        />
      </SimpleGrid>

      <SimpleGrid cols={{ base: 2, sm: 3 }} spacing="md">
        <Stat
          label={t('health.httpP95')}
          value={datadog.metrics.httpP95Ms == null ? '—' : `${datadog.metrics.httpP95Ms} ms`}
          sub={t('health.apm')}
        />
        <Stat
          label={t('health.llmSpend')}
          value={datadog.metrics.llmCostUsd == null ? '—' : usd(datadog.metrics.llmCostUsd, locale)}
          sub={t('health.llmObs')}
        />
        <Stat
          label={t('health.openErrors')}
          value={String(datadog.metrics.openIssueCount)}
          sub={t('health.errorTracking')}
        />
      </SimpleGrid>

      {!datadog.configured && (
        <Alert color="yellow" title={t('health.notConfigured')}>
          {datadog.error ?? t('health.notConfiguredBody')}
        </Alert>
      )}
      {datadog.configured && datadog.error && (
        <Alert color="red" title={t('health.queryFailed')}>
          {datadog.error}
        </Alert>
      )}

      {/* PRE-275: first thing on the screen, because it is the first question —
          is anything alerting right now. Unhappy monitors sort to the top. */}
      <Card withBorder padding="lg" radius="md">
        <Group justify="space-between" mb="sm">
          <Text fw={600}>{t('health.monitors')}</Text>
          {alerting.length > 0 && (
            <Badge color="red" variant="light">
              {t('health.firing', { count: alerting.length })}
            </Badge>
          )}
        </Group>
        {datadog.monitors.length === 0 ? (
          <Text c="dimmed" size="sm">
            {datadog.configured ? t('health.noMonitors') : '—'}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('health.colMonitor')}</Table.Th>
                  <Table.Th>{t('health.colState')}</Table.Th>
                  <Table.Th>{t('health.colChanged')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {datadog.monitors.map((m) => (
                  <Table.Tr key={m.id}>
                    <Table.Td>
                      <Text size="sm">{m.name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={monitorColor(m.status)} tt="none">
                        {m.status}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c="dimmed">
                        {fmtAgo(m.lastTriggeredMs, t)}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      {/* Trends. One series per card, so each card's title names its series and
          the hero number is the latest value — no legend, no shared palette. */}
      <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="md">
        <Sparkline
          label={t('health.requests')}
          points={datadog.trends.requests}
          sub={t('health.hourlyWindow', { hours: data.windowHours })}
        />
        <Sparkline
          label={t('health.errors')}
          points={datadog.trends.errors}
          tone="alert"
          sub={t('health.hourlyWindow', { hours: data.windowHours })}
        />
        <Sparkline
          label={t('health.httpP95')}
          points={datadog.trends.p95Ms}
          format={(v) => `${Math.round(v)} ms`}
          sub={t('health.hourlyWindow', { hours: data.windowHours })}
        />
      </SimpleGrid>

      {/* Recent failures. A fixed query, not a search box — reproducing the log
          explorer here would be a second product to maintain. */}
      <Card withBorder padding="lg" radius="md">
        <Text fw={600} mb="sm">
          {t('health.recentErrors')}
        </Text>
        {datadog.errorLogs.length === 0 ? (
          <Text c="dimmed" size="sm">
            {datadog.configured ? t('health.noErrorLogs') : '—'}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={720}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('health.colWhen')}</Table.Th>
                  <Table.Th>{t('health.colRoute')}</Table.Th>
                  <Table.Th>{t('health.colCode')}</Table.Th>
                  <Table.Th>{t('health.colMessage')}</Table.Th>
                  <Table.Th>{t('health.colTrace')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {datadog.errorLogs.map((log, i) => (
                  <Table.Tr key={`${log.timestampMs ?? 'x'}-${i}`}>
                    <Table.Td>
                      <Text size="sm">{fmtSeen(log.timestampMs, locale)}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm">{log.route ?? '—'}</Text>
                    </Table.Td>
                    <Table.Td>
                      {log.outcome ? (
                        <Badge color="red" variant="light" tt="none">
                          {log.outcome}
                        </Badge>
                      ) : (
                        <Text size="sm">{log.errorCode ?? '—'}</Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" lineClamp={2}>
                        {log.message}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      {log.traceId ? (
                        <Anchor href={traceUrl(log.traceId)} target="_blank" size="sm">
                          {t('health.openTrace')}
                        </Anchor>
                      ) : (
                        <Text size="sm" c="dimmed">
                          —
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      {/* Crons and queue consumers. Nothing could answer this before PRE-273: a
          cron that silently stopped looked exactly like one with nothing to do. */}
      <Card withBorder padding="lg" radius="md">
        <Text fw={600} mb="sm">
          {t('health.jobs')}
        </Text>
        {datadog.jobs.length === 0 ? (
          <Text c="dimmed" size="sm">
            {datadog.configured ? t('health.noJobLogs') : '—'}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={560}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('health.colJob')}</Table.Th>
                  <Table.Th>{t('health.colKind')}</Table.Th>
                  <Table.Th>{t('health.colLastRun')}</Table.Th>
                  <Table.Th ta="right">{t('health.colRuns')}</Table.Th>
                  <Table.Th ta="right">{t('health.colErrors')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {datadog.jobs.map((job) => (
                  <Table.Tr key={job.name}>
                    <Table.Td>
                      <Text size="sm">{job.name}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="default" tt="none">
                        {job.kind}
                      </Badge>
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" c={job.lastRunMs == null ? 'dimmed' : undefined}>
                        {fmtAgo(job.lastRunMs, t)}
                      </Text>
                    </Table.Td>
                    <Table.Td ta="right">{job.runs24h}</Table.Td>
                    <Table.Td ta="right">
                      <Text size="sm" c={job.errors24h > 0 ? 'red' : undefined}>
                        {job.errors24h}
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>

      <Card withBorder padding="lg" radius="md">
        <Text fw={600} mb="sm">
          {t('health.openErrors')}
        </Text>
        {datadog.issues.length === 0 ? (
          <Text c="dimmed" size="sm">
            {datadog.configured ? t('health.noIssues') : '—'}
          </Text>
        ) : (
          <Table.ScrollContainer minWidth={640}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('health.colError')}</Table.Th>
                  <Table.Th>{t('health.colState')}</Table.Th>
                  <Table.Th ta="right">{t('health.colEvents')}</Table.Th>
                  <Table.Th>{t('health.colLastSeen')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {datadog.issues.map((issue) => (
                  <Table.Tr key={issue.id}>
                    <Table.Td>
                      <Text size="sm">{issue.title}</Text>
                    </Table.Td>
                    <Table.Td>
                      <Badge variant="light" color={issueStateColor(issue.state)} tt="none">
                        {issue.state}
                      </Badge>
                    </Table.Td>
                    <Table.Td ta="right">{issue.events ?? '—'}</Table.Td>
                    <Table.Td>
                      <Text size="sm">{fmtSeen(issue.lastSeenMs, locale)}</Text>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Table.ScrollContainer>
        )}
      </Card>
    </Stack>
  );
}
